/**
 * The Good Patch
 * Custom storefront JavaScript
 *
 * 1. Normalizes Recharge delivery-frequency wording.
 * 2. Opens PDP galleries on media position #2.
 */

(() => {
  'use strict';

  /* =========================================================
     RECHARGE — NORMALIZE DELIVERY-FREQUENCY WORDING
     ========================================================= */

  const RECHARGE_WIDGET_SELECTOR = 'recharge-subscription-widget';

  const queryDeepAll = (root, selector, results = []) => {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return results;
    }

    root.querySelectorAll(selector).forEach((element) => {
      results.push(element);
    });

    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        queryDeepAll(element.shadowRoot, selector, results);
      }
    });

    return results;
  };

  const parseMoney = (value) => {
    if (!value) return null;

    const cleaned = String(value)
      .replace(/[^0-9.,-]/g, '')
      .replace(/,(?=\d{2}$)/, '.')
      .replace(/,/g, '');

    const number = Number.parseFloat(cleaned);

    return Number.isFinite(number) ? number : null;
  };

  const calculateRechargeDiscount = (root) => {
    const originalElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__original-price"]'
    )[0];

    const discountedElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__discounted-price"]'
    )[0];

    const original = parseMoney(originalElement?.textContent);
    const discounted = parseMoney(discountedElement?.textContent);

    if (
      original === null ||
      discounted === null ||
      original <= 0 ||
      discounted >= original
    ) {
      return '';
    }

    const percentage = Math.round(
      ((original - discounted) / original) * 100
    );

    return `${percentage}% off`;
  };

  const normalizeRechargeFrequency = (
    text,
    fallbackDiscount
  ) => {
    const originalText = String(text || '').trim();

    const intervalMatch = originalText.match(
      /(?:deliver\s+every\s+)?(\d+)\s+(day|week|month|year)s?/i
    );

    if (!intervalMatch) {
      return originalText;
    }

    const amount = Number(intervalMatch[1]);
    const unit = intervalMatch[2].toLowerCase();

    const intervalText =
      amount === 1
        ? `Deliver every ${unit}`
        : `Deliver every ${amount} ${unit}s`;

    const displayedDiscount = originalText.match(
      /\d+(?:\.\d+)?%\s*(?:off)?/i
    );

    const discountText = displayedDiscount
      ? `${displayedDiscount[0]
          .replace(/\s*off$/i, '')
          .trim()} off`
      : fallbackDiscount;

    return discountText
      ? `${intervalText}, ${discountText}`
      : intervalText;
  };

  const updateRechargeWidget = (widget) => {
    if (!widget?.shadowRoot) return;

    const discount = calculateRechargeDiscount(
      widget.shadowRoot
    );

    const selects = queryDeepAll(
      widget.shadowRoot,
      'select[part~="rc-plans-select"], [part~="rc-plans-select"]'
    );

    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      Array.from(select.options).forEach((option) => {
        const updatedText = normalizeRechargeFrequency(
          option.textContent,
          discount
        );

        if (option.textContent !== updatedText) {
          option.textContent = updatedText;
        }
      });
    });
  };

  const refreshRechargeWidgets = () => {
    document
      .querySelectorAll(RECHARGE_WIDGET_SELECTOR)
      .forEach(updateRechargeWidget);
  };

  let rechargeRefreshTimer = null;

  const scheduleRechargeRefresh = () => {
    window.clearTimeout(rechargeRefreshTimer);

    rechargeRefreshTimer = window.setTimeout(() => {
      refreshRechargeWidgets();
    }, 100);
  };

  const rechargeObserver = new MutationObserver(
    scheduleRechargeRefresh
  );

  const initializeRecharge = () => {
    rechargeObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    refreshRechargeWidgets();

    [250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(refreshRechargeWidgets, delay);
    });

    document.addEventListener(
      'change',
      scheduleRechargeRefresh,
      true
    );

    if ('customElements' in window) {
      customElements
        .whenDefined(RECHARGE_WIDGET_SELECTOR)
        .then(scheduleRechargeRefresh)
        .catch(() => {});
    }
  };


  /* =========================================================
     PDP — OPEN GALLERY ON MEDIA POSITION #2
     Keeps media #1 as the collection-card image.
     ========================================================= */

  const PRODUCT_TEMPLATE_CLASS = 'template-product';
  const MAX_GALLERY_ATTEMPTS = 30;
  const GALLERY_RETRY_DELAY = 150;

  const isProductPage = () =>
    document.body.classList.contains(
      PRODUCT_TEMPLATE_CLASS
    );

  const getMainGalleryElement = () =>
    document.querySelector(
      '.template-product .wt-product__gallery [data-gallery]'
    ) ||
    document.querySelector(
      '.template-product .wt-product__gallery .swiper'
    );

  const getGallerySwiper = () => {
    const gallery = getMainGalleryElement();

    if (!gallery) return null;

    return (
      gallery.swiper ||
      gallery.closest('.swiper')?.swiper ||
      gallery.querySelector('.swiper')?.swiper ||
      null
    );
  };

  const getGalleryThumbnails = () =>
    Array.from(
      document.querySelectorAll(
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.wt-slider__slide, ' +
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.swiper-slide'
      )
    );

  const openSecondProductMedia = () => {
    if (!isProductPage()) return true;

    const swiper = getGallerySwiper();

    if (swiper?.slides?.length > 1) {
      /*
       * Position #2 has zero-based index 1.
       * Use slideToLoop when the theme enables loop mode.
       */
      if (
        swiper.params?.loop &&
        typeof swiper.slideToLoop === 'function'
      ) {
        swiper.slideToLoop(1, 0, false);
      } else if (typeof swiper.slideTo === 'function') {
        swiper.slideTo(1, 0, false);
      }

      return true;
    }

    const thumbnails = getGalleryThumbnails();

    if (thumbnails.length > 1) {
      const secondThumbnail = thumbnails[1];
      const interactiveElement =
        secondThumbnail.querySelector(
          'button, a, [role="button"]'
        );

      if (interactiveElement) {
        interactiveElement.click();
      } else {
        secondThumbnail.click();
      }

      return true;
    }

    return false;
  };

  let galleryInitializationTimer = null;

  const initializeSecondProductMedia = () => {
    if (!isProductPage()) return;

    window.clearInterval(galleryInitializationTimer);

    let attempts = 0;

    galleryInitializationTimer = window.setInterval(() => {
      attempts += 1;

      const completed = openSecondProductMedia();

      if (
        completed ||
        attempts >= MAX_GALLERY_ATTEMPTS
      ) {
        window.clearInterval(
          galleryInitializationTimer
        );
      }
    }, GALLERY_RETRY_DELAY);
  };


    /* =========================================================
   RECHARGE — SYNC SELECTED PURCHASE PRICE TO PDP PRICE
   ========================================================= */

const rechargeWidgetObservers = new WeakMap();
let rechargePriceSyncTimer = null;

const getActiveRechargeWidget = () => {
  const widgets = Array.from(
    document.querySelectorAll(RECHARGE_WIDGET_SELECTOR)
  );

  return (
    widgets.find((widget) => {
      if (widget.hidden) return false;

      const styles = window.getComputedStyle(widget);

      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    }) ||
    widgets[0] ||
    null
  );
};

const getSelectedRechargePurchaseMode = (widget) => {
  if (!widget?.shadowRoot) return null;

  const selectedOption = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__selected"]'
  )[0];

  const selectedParts =
    selectedOption?.getAttribute('part') || '';

  if (
    selectedParts.includes(
      'rc-purchase-option__subscription'
    )
  ) {
    return 'subscription';
  }

  if (
    selectedParts.includes(
      'rc-purchase-option__onetime'
    )
  ) {
    return 'onetime';
  }

  const selectedInput = queryDeepAll(
    widget.shadowRoot,
    'input[part~="rc-purchase-option__input"]:checked'
  )[0];

  if (selectedInput) {
    const selectedText = [
      selectedInput.value,
      selectedInput.id,
      selectedInput.name,
      selectedInput.getAttribute('aria-label'),
      selectedInput.parentElement?.textContent
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/one[\s-]?time/.test(selectedText)) {
      return 'onetime';
    }

    if (/subscri|delivery/.test(selectedText)) {
      return 'subscription';
    }
  }

  const sellingPlanInput = document.querySelector(
    '.template-product form input[name="selling_plan"]'
  );

  if (sellingPlanInput) {
    return sellingPlanInput.value
      ? 'subscription'
      : 'onetime';
  }

  return null;
};

const getThemePriceHosts = () => {
  const finalPriceElements = Array.from(
    document.querySelectorAll(
      '.template-product .wt-product__main ' +
        '.wt-product__price__final, ' +
      '.template-product [id^="sticky-price-"] ' +
        '.wt-product__price__final'
    )
  );

  const hosts = finalPriceElements
    .map((element) => {
      let ancestor = element.parentElement;

      while (ancestor && ancestor !== document.body) {
        if (ancestor.classList.contains('wt-product__price')) {
          return ancestor;
        }

        ancestor = ancestor.parentElement;
      }

      return (
        element.closest('.price') ||
        element.parentElement
      );
    })
    .filter(Boolean);

  return Array.from(new Set(hosts));
};

const extractCleanMoney = (value) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return null;

  /*
   * Pull only the actual dollar amount from text such as:
   * "Discounted price: $34.00"
   * "Regular price $40.00"
   */
  const dollarMatches =
    normalized.match(
      /\$\s*\d[\d,]*(?:\.\d{1,2})?/g
    ) || [];

  const numericMatches =
    normalized.match(
      /\d[\d,]*(?:\.\d{1,2})?/g
    ) || [];

  let priceText =
    dollarMatches[dollarMatches.length - 1] ||
    numericMatches[numericMatches.length - 1] ||
    '';

  priceText = priceText
    .replace(/\s+/g, '')
    .trim();

  const priceValue = parseMoney(priceText);

  if (
    !priceText ||
    priceValue === null ||
    priceValue <= 0
  ) {
    return null;
  }

  /*
   * The Good Patch storefront is USD.
   * Add the dollar sign if the fallback numeric match
   * did not contain one.
   */
  if (!priceText.startsWith('$')) {
    priceText = `$${priceValue.toFixed(2)}`;
  }

  return {
    text: priceText,
    value: priceValue
  };
};

const getRechargeSubscriptionPrice = (widget) => {
  if (!widget?.shadowRoot) return null;

  const discountedPriceElement = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__discounted-price"]'
  )[0];

  const originalPriceElement = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__original-price"]'
  )[0];

  const priceElement =
    discountedPriceElement ||
    originalPriceElement;

  return extractCleanMoney(
    priceElement?.textContent
  );
};

const getThemePriceSourceData = (priceHost) => {
  const finalPriceElement =
    priceHost.querySelector(
      '.price__sale .wt-product__price__final'
    ) ||
    priceHost.querySelector(
      '.price__regular .wt-product__price__final'
    ) ||
    priceHost.querySelector(
      '.wt-product__price__final'
    );

  const compareAtElement =
    priceHost.querySelector(
      '.wt-product__price__compare'
    );

  const oneTimePrice =
    extractCleanMoney(
      finalPriceElement?.textContent
    );

  const compareAtPrice =
    extractCleanMoney(
      compareAtElement?.textContent
    );

  if (!oneTimePrice) {
    return null;
  }

  return {
    oneTimePriceText: oneTimePrice.text,
    oneTimePriceValue: oneTimePrice.value,
    compareAtPriceText:
      compareAtPrice?.text || '',
    compareAtPriceValue:
      compareAtPrice?.value ?? null
  };
};

const getSyncedPriceRow = (priceHost) => {
  const existingRow =
    priceHost.nextElementSibling;

  if (
    existingRow?.classList.contains(
      'tgp-synced-price-row'
    )
  ) {
    return existingRow;
  }

  const row = document.createElement('div');

  row.className = 'tgp-synced-price-row';
  row.setAttribute('role', 'status');
  row.setAttribute('aria-live', 'polite');
  row.setAttribute('aria-atomic', 'true');

  priceHost.insertAdjacentElement(
    'afterend',
    row
  );

  return row;
};

const renderSyncedPriceRow = ({
  priceHost,
  mode,
  finalPriceText,
  finalPriceValue,
  compareAtPriceText,
  compareAtPriceValue
}) => {
  const row = getSyncedPriceRow(priceHost);

  const validCompareAt =
    compareAtPriceValue !== null &&
    compareAtPriceValue > finalPriceValue;

  const savingsPercentage = validCompareAt
    ? Math.round(
        (
          (
            compareAtPriceValue -
            finalPriceValue
          ) /
          compareAtPriceValue
        ) * 100
      )
    : 0;

  const purchaseLabel =
    mode === 'subscription'
      ? 'Subscription price'
      : 'One-time purchase price';

  const accessibleText = validCompareAt
    ? `${purchaseLabel} ${finalPriceText}. ` +
      `Original price ${compareAtPriceText}. ` +
      `Save ${savingsPercentage} percent.`
    : `${purchaseLabel} ${finalPriceText}.`;

  const screenReaderText =
    document.createElement('span');

  screenReaderText.className =
    'tgp-synced-price-row__sr-only';

  screenReaderText.textContent = accessibleText;

  const visibleFragment =
    document.createDocumentFragment();

  if (validCompareAt) {
    const compareAt = document.createElement('s');

    compareAt.className =
      'tgp-synced-price-row__compare';

    compareAt.textContent = compareAtPriceText;
    compareAt.setAttribute('aria-hidden', 'true');

    visibleFragment.appendChild(compareAt);
  }

  const finalPrice = document.createElement('span');

  finalPrice.className =
    'tgp-synced-price-row__final';

  finalPrice.textContent = finalPriceText;
  finalPrice.setAttribute('aria-hidden', 'true');

  visibleFragment.appendChild(finalPrice);

  if (validCompareAt) {
    const savings = document.createElement('span');

    savings.className =
      'tgp-synced-price-row__savings';

    savings.textContent = `-${savingsPercentage}%`;
    savings.setAttribute('aria-hidden', 'true');

    visibleFragment.appendChild(savings);
  }

  row.replaceChildren(
    screenReaderText,
    visibleFragment
  );

  row.dataset.tgpPriceMode = mode;
  row.dataset.tgpPriceValue =
    String(finalPriceValue);

  priceHost.classList.add(
    'tgp-theme-price--hidden'
  );

  priceHost.setAttribute('aria-hidden', 'true');
};

const restoreThemePrices = () => {
  document
    .querySelectorAll(
      '.template-product .tgp-theme-price--hidden'
    )
    .forEach((priceHost) => {
      priceHost.classList.remove(
        'tgp-theme-price--hidden'
      );

      priceHost.removeAttribute('aria-hidden');
    });

  document
    .querySelectorAll(
      '.template-product .tgp-synced-price-row'
    )
    .forEach((row) => row.remove());
};


/* =========================================================
   RECHARGE VARIATION B — GLOBAL DYNAMIC WIDGET PRICING
   Applies only when the widget badge reads "SUBSCRIBE & SAVE:".
   Uses the selected variant's Shopify price and compare-at price.
   ========================================================= */

const VARIATION_A_BADGE_TEXT = 'best value';
const VARIATION_B_BADGE_TEXT = 'subscribe & save:';

const normalizeWidgetText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getPartElement = (root, partName) =>
  queryDeepAll(
    root,
    `[part~="${partName}"]`
  )[0] || null;

const addPartToken = (element, token) => {
  if (!element) return;

  const tokens = new Set(
    String(element.getAttribute('part') || '')
      .split(/\s+/)
      .filter(Boolean)
  );

  tokens.add(token);
  element.setAttribute(
    'part',
    Array.from(tokens).join(' ')
  );
};

const removePartToken = (element, token) => {
  if (!element) return;

  const tokens = String(
    element.getAttribute('part') || ''
  )
    .split(/\s+/)
    .filter(
      (currentToken) =>
        currentToken &&
        currentToken !== token
    );

  if (tokens.length) {
    element.setAttribute(
      'part',
      tokens.join(' ')
    );
  } else {
    element.removeAttribute('part');
  }
};

const getRechargeWidgetBadgeText = (widget) => {
  if (!widget?.shadowRoot) return '';

  const badge = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__badge'
  );

  return normalizeWidgetText(
    badge?.textContent
  );
};

const isVariationARechargeWidget = (widget) =>
  getRechargeWidgetBadgeText(widget) ===
  VARIATION_A_BADGE_TEXT;

const isVariationBRechargeWidget = (widget) =>
  getRechargeWidgetBadgeText(widget) ===
  VARIATION_B_BADGE_TEXT;

const getPrimaryThemePriceSourceData = () => {
  const priceHosts = getThemePriceHosts();

  if (!priceHosts.length) return null;

  const mainPriceHost =
    priceHosts.find(
      (priceHost) =>
        !String(priceHost.id || '').startsWith(
          'sticky-price-'
        )
    ) || priceHosts[0];

  return getThemePriceSourceData(mainPriceHost);
};

const restoreVariationAComparePrice = (widget) => {
  if (!widget?.shadowRoot) return;

  const originalPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  if (!originalPrice) return;

  const nativePriceText =
    originalPrice.dataset
      .tgpNativeRechargeOriginalPrice;

  if (nativePriceText) {
    originalPrice.textContent =
      nativePriceText;
  }

  delete originalPrice.dataset
    .tgpVariationAComparePrice;

  originalPrice.removeAttribute(
    'aria-label'
  );

  queryDeepAll(
    widget.shadowRoot,
    '[part~="tgp-recharge-a-onetime-prices"]'
  ).forEach((element) => element.remove());

  const nativeOneTimePrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__price'
  );

  nativeOneTimePrice?.removeAttribute(
    'aria-hidden'
  );

  widget.classList.remove(
    'tgp-recharge-variation-a'
  );
};

const syncVariationAComparePrice = (widget) => {
  if (!widget?.shadowRoot) return;

  if (!isVariationARechargeWidget(widget)) {
    restoreVariationAComparePrice(widget);
    return;
  }

  const sourceData =
    getPrimaryThemePriceSourceData();

  const subscriptionPrice =
    getRechargeSubscriptionPrice(widget);

  const originalPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  if (
    !sourceData ||
    !subscriptionPrice ||
    !originalPrice
  ) {
    return;
  }

  const currentText = String(
    originalPrice.textContent || ''
  ).trim();

  const lastAppliedText =
    originalPrice.dataset
      .tgpVariationAComparePrice || '';

  /*
   * Preserve Recharge's native comparison value. This lets
   * us restore the correct native price when a selected
   * variant has no Shopify compare-at price.
   */
  if (
    currentText &&
    currentText !== lastAppliedText
  ) {
    originalPrice.dataset
      .tgpNativeRechargeOriginalPrice =
      currentText;
  }

  const hasValidShopifyCompareAt =
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      subscriptionPrice.value;

  if (!hasValidShopifyCompareAt) {
    restoreVariationAComparePrice(widget);
    return;
  }

  const compareAtText =
    sourceData.compareAtPriceText;

  if (
    originalPrice.textContent !==
    compareAtText
  ) {
    originalPrice.textContent =
      compareAtText;
  }

  originalPrice.dataset
    .tgpVariationAComparePrice =
    compareAtText;

  originalPrice.setAttribute(
    'aria-label',
    `Original price ${compareAtText}`
  );

  widget.classList.add(
    'tgp-recharge-variation-a'
  );

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const nativeOneTimePrice = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__price'
      )
    : getPartElement(
        widget.shadowRoot,
        'rc-purchase-option__price'
      );

  const hasValidOneTimeCompareAt =
    oneTimeOption &&
    nativeOneTimePrice &&
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      sourceData.oneTimePriceValue;

  if (!hasValidOneTimeCompareAt) {
    queryDeepAll(
      widget.shadowRoot,
      '[part~="tgp-recharge-a-onetime-prices"]'
    ).forEach((element) => element.remove());

    nativeOneTimePrice?.removeAttribute(
      'aria-hidden'
    );

    return;
  }

  let priceGroup = queryDeepAll(
    widget.shadowRoot,
    '[part~="tgp-recharge-a-onetime-prices"]'
  )[0];

  if (!priceGroup) {
    priceGroup = document.createElement('span');
    priceGroup.setAttribute(
      'part',
      'tgp-recharge-a-onetime-prices'
    );
    oneTimeOption.appendChild(priceGroup);
  }

  let currentPrice = queryDeepAll(
    priceGroup,
    '[part~="tgp-recharge-a-onetime-current"]'
  )[0];

  if (!currentPrice) {
    currentPrice = document.createElement('span');
    currentPrice.setAttribute(
      'part',
      'tgp-recharge-a-onetime-current'
    );
    priceGroup.appendChild(currentPrice);
  }

  let comparePrice = queryDeepAll(
    priceGroup,
    '[part~="tgp-recharge-a-onetime-compare"]'
  )[0];

  if (!comparePrice) {
    comparePrice = document.createElement('s');
    comparePrice.setAttribute(
      'part',
      'tgp-recharge-a-onetime-compare'
    );
    priceGroup.appendChild(comparePrice);
  }

  currentPrice.textContent =
    sourceData.oneTimePriceText;

  comparePrice.textContent =
    sourceData.compareAtPriceText;

  nativeOneTimePrice.setAttribute(
    'aria-hidden',
    'true'
  );

  priceGroup.setAttribute(
    'aria-label',
    `One-time price ${sourceData.oneTimePriceText}. ` +
      `Original price ${sourceData.compareAtPriceText}.`
  );

  currentPrice.setAttribute(
    'aria-hidden',
    'true'
  );

  comparePrice.setAttribute(
    'aria-hidden',
    'true'
  );
};


/* =========================================================
   RECHARGE VARIATION B — GLOBAL DYNAMIC WIDGET PRICING
   ========================================================= */

const removeVariationBInjectedElements = (widget) => {
  if (!widget?.shadowRoot) return;

  queryDeepAll(
    widget.shadowRoot,
    [
      '[part~="tgp-recharge-subscription-compare"]',
      '[part~="tgp-recharge-save-badge"]',
      '[part~="tgp-recharge-onetime-prices"]'
    ].join(', ')
  ).forEach((element) => element.remove());

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  originalSubscriptionPrice?.removeAttribute(
    'aria-hidden'
  );

  const defaultOneTimePrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__price'
  );

  defaultOneTimePrice?.removeAttribute(
    'aria-hidden'
  );

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const oneTimeLabel = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__label'
      )
    : null;

  removePartToken(
    oneTimeLabel,
    'tgp-recharge-onetime-label'
  );

  widget.classList.remove(
    'tgp-recharge-variation-b'
  );
};

const getOrCreateVariationBElement = ({
  root,
  selector,
  tagName,
  partName,
  parent
}) => {
  let element = queryDeepAll(
    root,
    selector
  )[0];

  if (!element) {
    element = document.createElement(tagName);
    element.setAttribute('part', partName);
    parent.appendChild(element);
  }

  return element;
};

const syncVariationBRechargePricing = (widget) => {
  if (!widget?.shadowRoot) return;

  if (!isVariationBRechargeWidget(widget)) {
    removeVariationBInjectedElements(widget);
    return;
  }

  const sourceData =
    getPrimaryThemePriceSourceData();

  const subscriptionPrice =
    getRechargeSubscriptionPrice(widget);

  if (!sourceData || !subscriptionPrice) {
    return;
  }

  widget.classList.add(
    'tgp-recharge-variation-b'
  );

  const priceRow = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__prices'
  );

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  if (priceRow) {
    originalSubscriptionPrice?.setAttribute(
      'aria-hidden',
      'true'
    );

    const validSubscriptionCompareAt =
      sourceData.compareAtPriceValue !== null &&
      sourceData.compareAtPriceValue >
        subscriptionPrice.value;

    if (validSubscriptionCompareAt) {
      const compareAt =
        getOrCreateVariationBElement({
          root: widget.shadowRoot,
          selector:
            '[part~="tgp-recharge-subscription-compare"]',
          tagName: 's',
          partName:
            'tgp-recharge-subscription-compare',
          parent: priceRow
        });

      if (
        compareAt.textContent !==
        sourceData.compareAtPriceText
      ) {
        compareAt.textContent =
          sourceData.compareAtPriceText;
      }

      compareAt.setAttribute(
        'aria-label',
        `Original price ${sourceData.compareAtPriceText}`
      );

      const savingsPercentage = Math.round(
        (
          (
            sourceData.compareAtPriceValue -
            subscriptionPrice.value
          ) /
          sourceData.compareAtPriceValue
        ) * 100
      );

      const savingsBadge =
        getOrCreateVariationBElement({
          root: widget.shadowRoot,
          selector:
            '[part~="tgp-recharge-save-badge"]',
          tagName: 'span',
          partName:
            'tgp-recharge-save-badge',
          parent: priceRow
        });

      const savingsText =
        `SAVE ${savingsPercentage}%`;

      if (
        savingsBadge.textContent !==
        savingsText
      ) {
        savingsBadge.textContent =
          savingsText;
      }
    } else {
      queryDeepAll(
        widget.shadowRoot,
        [
          '[part~="tgp-recharge-subscription-compare"]',
          '[part~="tgp-recharge-save-badge"]'
        ].join(', ')
      ).forEach((element) => element.remove());
    }
  }

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  if (!oneTimeOption) return;

  const oneTimeLabel = getPartElement(
    oneTimeOption,
    'rc-purchase-option__label'
  );

  addPartToken(
    oneTimeLabel,
    'tgp-recharge-onetime-label'
  );

  const defaultOneTimePrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__price'
  );

  defaultOneTimePrice?.setAttribute(
    'aria-hidden',
    'true'
  );

  const priceGroup =
    getOrCreateVariationBElement({
      root: widget.shadowRoot,
      selector:
        '[part~="tgp-recharge-onetime-prices"]',
      tagName: 'span',
      partName:
        'tgp-recharge-onetime-prices',
      parent: oneTimeLabel || oneTimeOption
    });

  /*
   * Recharge nests its default one-time price inside the
   * real option label. Keep our dynamic current/compare pair
   * in that same label so the complete option remains one
   * horizontal row on every product and variant.
   */
  if (
    oneTimeLabel &&
    priceGroup.parentElement !== oneTimeLabel
  ) {
    oneTimeLabel.appendChild(priceGroup);
  }

  const currentOneTimePrice =
    getOrCreateVariationBElement({
      root: widget.shadowRoot,
      selector:
        '[part~="tgp-recharge-onetime-current"]',
      tagName: 'span',
      partName:
        'tgp-recharge-onetime-current',
      parent: priceGroup
    });

  if (
    currentOneTimePrice.textContent !==
    sourceData.oneTimePriceText
  ) {
    currentOneTimePrice.textContent =
      sourceData.oneTimePriceText;
  }

  const validOneTimeCompareAt =
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      sourceData.oneTimePriceValue;

  if (validOneTimeCompareAt) {
    const oneTimeCompareAt =
      getOrCreateVariationBElement({
        root: widget.shadowRoot,
        selector:
          '[part~="tgp-recharge-onetime-compare"]',
        tagName: 's',
        partName:
          'tgp-recharge-onetime-compare',
        parent: priceGroup
      });

    if (
      oneTimeCompareAt.textContent !==
      sourceData.compareAtPriceText
    ) {
      oneTimeCompareAt.textContent =
        sourceData.compareAtPriceText;
    }

    priceGroup.setAttribute(
      'aria-label',
      `One-time price ${sourceData.oneTimePriceText}. ` +
        `Original price ${sourceData.compareAtPriceText}.`
    );
  } else {
    queryDeepAll(
      priceGroup,
      '[part~="tgp-recharge-onetime-compare"]'
    ).forEach((element) => element.remove());

    priceGroup.setAttribute(
      'aria-label',
      `One-time price ${sourceData.oneTimePriceText}.`
    );
  }

  queryDeepAll(
    priceGroup,
    [
      '[part~="tgp-recharge-onetime-current"]',
      '[part~="tgp-recharge-onetime-compare"]'
    ].join(', ')
  ).forEach((element) => {
    element.setAttribute(
      'aria-hidden',
      'true'
    );
  });
};

const syncProductPriceWithRecharge = () => {
  if (
    !document.body.classList.contains(
      'template-product'
    )
  ) {
    return;
  }

  const widget = getActiveRechargeWidget();

  if (!widget?.shadowRoot) {
    restoreThemePrices();
    return;
  }

  syncVariationAComparePrice(widget);
  syncVariationBRechargePricing(widget);

  const purchaseMode =
    getSelectedRechargePurchaseMode(widget);

  const priceHosts = getThemePriceHosts();

  if (!priceHosts.length) return;

  const subscriptionPrice =
    purchaseMode === 'subscription'
      ? getRechargeSubscriptionPrice(widget)
      : null;

  if (
    purchaseMode === 'subscription' &&
    !subscriptionPrice
  ) {
    restoreThemePrices();
    return;
  }

  priceHosts.forEach((priceHost) => {
    const sourceData =
      getThemePriceSourceData(priceHost);

    if (!sourceData) return;

    if (purchaseMode === 'subscription') {
      renderSyncedPriceRow({
        priceHost,
        mode: 'subscription',
        finalPriceText: subscriptionPrice.text,
        finalPriceValue: subscriptionPrice.value,
        compareAtPriceText:
          sourceData.compareAtPriceText,
        compareAtPriceValue:
          sourceData.compareAtPriceValue
      });

      return;
    }

    renderSyncedPriceRow({
      priceHost,
      mode: 'onetime',
      finalPriceText:
        sourceData.oneTimePriceText,
      finalPriceValue:
        sourceData.oneTimePriceValue,
      compareAtPriceText:
        sourceData.compareAtPriceText,
      compareAtPriceValue:
        sourceData.compareAtPriceValue
    });
  });
};

const scheduleRechargePriceSync = (
  delay = 80
) => {
  window.clearTimeout(rechargePriceSyncTimer);

  rechargePriceSyncTimer =
    window.setTimeout(() => {
      observeRechargePriceWidgets();
      syncProductPriceWithRecharge();
    }, delay);
};

const observeRechargePriceWidget = (widget) => {
  if (!widget?.shadowRoot) return;

  const existing =
    rechargeWidgetObservers.get(widget);

  if (existing?.root === widget.shadowRoot) {
    return;
  }

  existing?.observer?.disconnect();

  const observer = new MutationObserver(() => {
    scheduleRechargePriceSync(40);
  });

  observer.observe(widget.shadowRoot, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      'part',
      'checked',
      'aria-checked',
      'class',
      'value'
    ]
  });

  widget.shadowRoot.addEventListener(
    'change',
    () => scheduleRechargePriceSync(20),
    true
  );

  widget.shadowRoot.addEventListener(
    'click',
    () => scheduleRechargePriceSync(40),
    true
  );

  rechargeWidgetObservers.set(widget, {
    root: widget.shadowRoot,
    observer
  });
};

const observeRechargePriceWidgets = () => {
  document
    .querySelectorAll(RECHARGE_WIDGET_SELECTOR)
    .forEach(observeRechargePriceWidget);
};

const initializeRechargePriceSync = () => {
  const widgetMountObserver =
    new MutationObserver(() => {
      observeRechargePriceWidgets();
    });

  widgetMountObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  observeRechargePriceWidgets();

  [100, 300, 750, 1500, 3000].forEach(
    (delay) => {
      window.setTimeout(() => {
        observeRechargePriceWidgets();
        syncProductPriceWithRecharge();
      }, delay);
    }
  );

  document.addEventListener(
    'variantChangeEnd',
    () => {
      restoreThemePrices();

      [50, 200, 600].forEach((delay) => {
        window.setTimeout(
          syncProductPriceWithRecharge,
          delay
        );
      });
    }
  );

  document.addEventListener(
    'shopify:section:load',
    () => {
      restoreThemePrices();
      scheduleRechargePriceSync(200);
    }
  );
};


/* =========================================================
   INITIALIZATION
   ========================================================= */

const initializeCustomScripts = () => {
  initializeRecharge();
  initializeRechargePriceSync();
  initializeSecondProductMedia();
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeCustomScripts,
    { once: true }
  );
} else {
  initializeCustomScripts();
}

/*
 * Reinitialize when Shopify reloads a section inside
 * the theme editor.
 */
document.addEventListener(
  'shopify:section:load',
  () => {
    scheduleRechargeRefresh();
    initializeSecondProductMedia();
  }
);

document.addEventListener(
  'shopify:block:select',
  () => {
    scheduleRechargeRefresh();
  }
);

})();